<template>
  <div class="p-6">
    <!-- Apple Device App Store Banner -->
    <div v-if="isAppleDevice" class="max-w-2xl mx-auto mb-8 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 rounded-2xl shadow-xl overflow-hidden">
      <div class="flex flex-col sm:flex-row items-center gap-4 p-5 sm:p-6">
        <div class="shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg ring-2 ring-white/20">
          <span class="text-white font-bold text-2xl">S</span>
        </div>
        <div class="flex-1 text-center sm:text-left">
          <p class="text-white font-semibold text-base leading-snug">
            {{ $t('AppleDeviceWelcome') }}
          </p>
          <p class="text-gray-400 text-sm mt-1">
            {{ $t('AppleDeviceSubtitle', { device: deviceName }) }}
          </p>
        </div>
        <a :href="appStoreUrl" target="_blank" rel="noopener noreferrer"
           class="shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-white text-gray-900 font-semibold text-sm rounded-full hover:bg-gray-100 transition-colors shadow-md hover:shadow-lg">
          <svg class="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
          {{ $t('DownloadOnAppStore') }}
        </a>
      </div>
    </div>

    <div class="text-center mb-10" v-if="!inDialog">
      <h1 class="text-2xl font-bold text-gray-900 mb-4">{{ $t('ChoosePlan') }}</h1>
      <div class="inline-block px-6 py-3 bg-gradient-to-r from-amber-400 via-orange-400 to-pink-500 rounded-full shadow-lg">
        <p class="text-white font-bold text-base flex items-center gap-2">
          <span>🎁</span>
          {{ $t("PayHint") }}
          <span>✨</span>
        </p>
      </div>
    </div>

    <!-- Pricing Grid -->
    <div class="grid grid-cols-3 gap-4 max-w-3xl mx-auto">
      <div
        v-for="plan in plans"
        :key="plan.id"
        class="relative border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-300 bg-white flex flex-col items-center group cursor-default"
        :class="{'border-indigo-500 ring-1 ring-indigo-500 shadow-md': plan.isRecommended, 'opacity-90 hover:opacity-100': !plan.isRecommended}"
      >
        <!-- Badge -->
        <div v-if="plan.isRecommended" class="absolute -top-3 bg-indigo-500 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
           {{ $t('Recommended') }}
        </div>

        <div class="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
           <img :src="VIP" class="w-8 h-8 object-contain" loading="lazy" />
        </div>

        <h3 class="text-lg font-bold text-gray-900 mb-1">{{ $t(plan.titleKey) }}</h3>

        <div class="flex flex-col items-center mb-4">
           <span class="text-3xl font-bold text-gray-900 leading-tight">{{ prices[plan.priceId]?.formattedPrice || plan.price }}</span>
           <span class="text-sm text-gray-500 mt-1">{{ $t(plan.timeKey) }}</span>
        </div>

        <p class="text-sm text-gray-500 text-center mb-6 min-h-[40px]">
           {{ $t(plan.hintKey) }}
        </p>

        <el-button
           :type="plan.buttonType"
           class="w-full !rounded-lg !h-10 !font-semibold"
           :plain="!plan.isRecommended"
           :loading="isProcessing"
           @click="openCheckout(plan)"
        >
           {{ $t(plan.buttonKey || 'Subscribe') }}
        </el-button>
      </div>
    </div>

    <div v-if="!inDialog" class="mt-12 text-center border-t border-gray-100 pt-8">
      <p class="text-xs text-gray-400">Secure payment via Paddle · Cancel anytime</p>
    </div>
  </div>
</template>

<script>
import VIP from '@/assets/vip.png'
import { useAppleDevice } from '@/composables/useAppleDevice'

export default {
  name: 'PricePage',
  props: {
    inDialog: Boolean
  },
  setup() {
    const { isAppleDevice, deviceName, appStoreUrl } = useAppleDevice()
    return { isAppleDevice, deviceName, appStoreUrl }
  },
  data() {
    return {
      VIP,
      loading: false,
      fetchingPrices: false,
      checkingSubscription: false,
      isProcessing: false,
      prices: {},
      plans: [
        {
          id: 'annual',
          priceId: 'pri_01k24bwzgbc2ex2yc6vpx4xvx0',
          price: '$3.99',
          titleKey: 'AnnualVIP',
          timeKey: 'AnnualVIPTime',
          hintKey: 'AnnualVIPHint',
          buttonType: 'primary',
          days: 365,
          isRecommended: true
        },
        {
          id: 'monthly',
          priceId: 'pri_01k24bvf2fpz9zpedh7stf6tvr',
          price: '$1.99',
          titleKey: 'MonthlyVIP',
          timeKey: 'MonthlyVIPTime',
          hintKey: 'MonthlyVIPHint',
          buttonType: 'primary',
          days: 31,
          isRecommended: false
        },
        {
          id: 'daily',
          priceId: 'pri_01j9pfe9ytzq707g7c5vsxj1ys',
          price: '$0.7',
          titleKey: 'DailyPlan',
          timeKey: 'DailyPlanTime',
          hintKey: 'DailyPlanHint',
          buttonKey: 'Pay',
          buttonType: 'primary',
          days: 1,
          isRecommended: false
        }
      ]
    }
  },
  async mounted() {
    this.loading = true
    try {
      await this.initializePaddle()
      await this.fetchPrices()
      this.checkSubscriptionStatus()
    } catch (error) {
      console.error('初始化失败:', error)
      this.$message.error(this.$t('InitError'))
    } finally {
      this.loading = false
    }
  },
  methods: {
    async initializePaddle() {
      // eslint-disable-next-line no-undef
      Paddle.Initialize({
        token: 'live_984f275269b63f04e8cd9d60ee1',
        eventCallback: this.handlePaddleEvent
      })
    },

    async fetchPrices() {
      this.fetchingPrices = true
      try {
        const items = this.plans.map(plan => ({
          priceId: plan.priceId,
          quantity: 1
        }))

        // eslint-disable-next-line no-undef
        const result = await Paddle.PricePreview({ items })

        this.prices = result.data.details.lineItems.reduce((acc, item) => {
          acc[item.price.id] = {
            formattedPrice: item.formattedTotals.total,
            amount: item.totals.total
          }
          return acc
        }, {})
      } catch (error) {
        console.error('价格获取失败:', error)
        this.$message.warning(this.$t('PriceFetchError'))
      } finally {
        this.fetchingPrices = false
      }
    },

    checkSubscriptionStatus() {
      const purchaseStatus = localStorage.getItem('purchaseStatus')
      const expirationTime = localStorage.getItem('expirationTime')

      if (purchaseStatus === 'purchased' && expirationTime) {
        const expDate = new Date(expirationTime)
        if (expDate > new Date()) {
          return
        }
      }

      localStorage.removeItem('purchaseStatus')
      localStorage.removeItem('expirationTime')
    },

    async openCheckout(plan) {
      this.isProcessing = true

      await this.$nextTick()

      try {
        // eslint-disable-next-line no-undef
        const checkoutResult = await Paddle.Checkout.open({
          items: [{
            priceId: plan.priceId,
            quantity: 1
          }],
          settings: {
            displayMode: 'overlay',
            theme: 'light',
            locale: this.$i18n.locale
          }
        })

        if (checkoutResult) {
          this.$message.success(this.$t('CheckoutOpened'))
        }
      } catch (error) {
        console.error('支付发起失败:', error)
        this.$message.error(this.$t('CheckoutError'))
      } finally {
        this.isProcessing = false
      }
    },

    handlePaddleEvent(data) {
      if (data.name === 'checkout.completed') {
        const item = data.data.items[0]
        const plan = this.plans.find(p => p.priceId === item.priceId)

        if (plan) {
          const expirationTime = new Date()
          expirationTime.setDate(expirationTime.getDate() + plan.days)

          localStorage.setItem('purchaseStatus', 'purchased')
          localStorage.setItem('plan_id', plan.id)
          localStorage.setItem('expirationTime', expirationTime.toISOString())

          this.$message.success(this.$t('PurchaseSuccess'))
        }
      }
    }
  }
};
</script>
